import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction, AnyAction } from '@reduxjs/toolkit';

import { InitialLoadError } from '@/modules/partner/actions/async/errors';
import { PartnerBlockedError } from '@/modules/partner/api';
import { errorHandling } from '@/services/errorHandling';
import { LoginRequiredError, NotFoundError } from '@/shared/api/fetch-based-http-methods';
import { ValidationError } from '@src-common/api/src/validate';

import { namespace as appNamespace } from '../namespace';

import { ERROR, TError } from './fatalErrors';

type TErrorHandler = (message: string) => void;

let handleError: TErrorHandler = () => undefined;

const stringify = (some: any) => {
  try {
    return JSON.stringify(some, null, 2);
  } catch {
    return String(some);
  }
};

const initialState = {
  isAppInitialized: false,
  fatalError: undefined as undefined | TError,
};

const callNextTick = <T extends (...args: any) => any>(
  handler: T,
  ...args: Parameters<T>
): void => {
  setTimeout(() => handler(args));
};

const createNextTickHandler =
  (handler: TErrorHandler): TErrorHandler =>
  message =>
    callNextTick(handler, message);

const appSlice = createSlice({
  name: appNamespace,
  initialState,
  reducers: {
    setErrorHandler: (state, action: PayloadAction<TErrorHandler>) => {
      handleError = createNextTickHandler(action.payload);
    },
    showError: (state, action: PayloadAction<string>) => {
      handleError(action.payload);
    },
    setIsAppInitialized: state => {
      state.isAppInitialized = true;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      (action: AnyAction) => action.error,
      (state, action) => {
        // action contains copy of error, so we can not use instanceOf

        if (LoginRequiredError.is(action.error)) {
          state.fatalError = ERROR.LOGIN_REQUIRED;

          return;
        }

        if (PartnerBlockedError.is(action.error)) {
          state.fatalError = ERROR.BLOCKED;

          return;
        }

        if (NotFoundError.is(action.error)) {
          state.fatalError = ERROR.NOT_FOUND;

          return;
        }

        errorHandling.captureException(action.error);

        if (InitialLoadError.is(action.error)) {
          state.fatalError = ERROR.INIT_LOAD_FAIL;

          return;
        }

        if (ValidationError.is(action.error)) {
          state.fatalError = ERROR.API_ERROR;

          return;
        }

        handleError(
          typeof action.error.message === 'string' ? action.error.message : stringify(action.error),
        );
      },
    );
  },
});

export { appSlice, appNamespace };
